import { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { AsanaAuthorizationResponseStatusEnum } from "../lusca-search-api/generated-src";
import { ChatUatApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

export const ASANA_OAUTH_STATE_KEY = "chatUat.asana.oauth.state";
export const ASANA_OAUTH_RETURN_KEY = "chatUat.asana.oauth.return";
export const ASANA_OAUTH_STARTED_AT_KEY = "chatUat.asana.oauth.startedAt";
export const ASANA_OAUTH_SESSION_ID_KEY = "chatUat.asana.oauth.sessionId";
export const ASANA_OAUTH_CONNECTED_KEY = "chatUat.asana.oauth.connected";
export const ASANA_OAUTH_MAX_AGE_MS = 10 * 60 * 1000;

function randomState(): string {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join(
    ""
  );
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const value: unknown = error.response?.data;
    if (
      typeof value === "object" &&
      value !== null &&
      "error" in value &&
      typeof (value as { error?: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return "UAT Insights could not connect to Asana.";
}

export function isAsanaAuthorizationError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

export default function useAsanaAuthorization() {
  const api = useMemo(() => ChatUatApiFactory(), []);
  const [isAuthorized, setIsAuthorized] = useState(
    () => sessionStorage.getItem(ASANA_OAUTH_CONNECTED_KEY) === "true"
  );
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [authorizationError, setAuthorizationError] = useState<string>();

  const markAuthorized = useCallback(() => {
    sessionStorage.setItem(ASANA_OAUTH_CONNECTED_KEY, "true");
    setIsAuthorized(true);
    setNeedsReconnect(false);
    setAuthorizationError(undefined);
  }, []);

  const markUnauthorized = useCallback(() => {
    sessionStorage.removeItem(ASANA_OAUTH_CONNECTED_KEY);
    setIsAuthorized(false);
    setNeedsReconnect(true);
  }, []);

  /**
   * Resolves true when Asana is already connected, or false when the page is
   * being sent to Asana to authorize.
   */
  const beginAuthorization = useCallback(
    async (forceAuthentication = false): Promise<boolean> => {
      const state = randomState();
      const returnPath = `${window.location.pathname}${window.location.search}`;
      sessionStorage.setItem(ASANA_OAUTH_STATE_KEY, state);
      sessionStorage.setItem(ASANA_OAUTH_RETURN_KEY, returnPath);
      sessionStorage.setItem(ASANA_OAUTH_STARTED_AT_KEY, String(Date.now()));
      setAuthorizationError(undefined);
      setIsConnecting(true);
      try {
        const response = (
          await api.beginChatUatAsanaAuthorization({
            state,
            forceAuthentication,
          })
        ).data;
        if (
          response.status === AsanaAuthorizationResponseStatusEnum.Connected
        ) {
          sessionStorage.removeItem(ASANA_OAUTH_STATE_KEY);
          sessionStorage.removeItem(ASANA_OAUTH_RETURN_KEY);
          sessionStorage.removeItem(ASANA_OAUTH_STARTED_AT_KEY);
          sessionStorage.removeItem(ASANA_OAUTH_SESSION_ID_KEY);
          markAuthorized();
          return true;
        }
        if (
          response.status !==
            AsanaAuthorizationResponseStatusEnum.AuthorizationRequired ||
          !response.authorizationUrl ||
          !response.sessionId
        ) {
          throw new Error("Invalid Asana authorization response");
        }
        const authorizationUrl = new URL(response.authorizationUrl);
        if (authorizationUrl.protocol !== "https:") {
          throw new Error("Invalid Asana authorization URL");
        }
        sessionStorage.setItem(ASANA_OAUTH_SESSION_ID_KEY, response.sessionId);
        window.location.assign(authorizationUrl.toString());
        return false;
      } catch (error: unknown) {
        sessionStorage.removeItem(ASANA_OAUTH_STATE_KEY);
        sessionStorage.removeItem(ASANA_OAUTH_RETURN_KEY);
        sessionStorage.removeItem(ASANA_OAUTH_STARTED_AT_KEY);
        sessionStorage.removeItem(ASANA_OAUTH_SESSION_ID_KEY);
        setAuthorizationError(errorMessage(error));
        throw error;
      } finally {
        setIsConnecting(false);
      }
    },
    [api, markAuthorized]
  );

  return {
    isAuthorized,
    needsReconnect,
    isConnecting,
    authorizationError,
    beginAuthorization,
    markAuthorized,
    markUnauthorized,
  };
}
